import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Snackbar, Alert } from '@mui/material';
import { Layout as AntLayout, Menu, Button, ConfigProvider, theme as antTheme } from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    SwapOutlined,
    LineChartOutlined,
    LogoutOutlined,
    BulbOutlined,
    BulbFilled,
    PieChartOutlined,
    FileSearchOutlined,
    DollarOutlined,
} from '@ant-design/icons';
import Login from './components/Login';
import BrandLogo from './components/BrandLogo';
import { alexforbesBrand, alexforbesAntTheme, applyAlexforbesCssVariables } from './brand/alexforbes';
import TradeList from './components/TradeList';
import InstrumentList from './components/InstrumentList';
import CurrencyList from './components/CurrencyList';
import PortfolioList from './components/PortfolioList';
import PortfolioDetail from './components/PortfolioDetail';
import ReconPage from './components/ReconPage';
import { useAuth } from './contexts/AuthContext';
import { useSessionTimeout, SessionTimeoutWarning } from './utils/authUtils';
import './App.css';

const { Header, Sider, Content } = AntLayout;
const { useToken } = antTheme;

const { colors: afColors } = alexforbesBrand;

function App() {
    const { isAuthenticated, authReady, logout } = useAuth();
    const [collapsed, setCollapsed] = useState(false);
    const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertSeverity, setAlertSeverity] = useState('info');
    const location = useLocation();
    const navigate = useNavigate();
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme === 'dark';
    });

    useEffect(() => {
        applyAlexforbesCssVariables();
        document.body.style.backgroundColor = isDarkMode
            ? alexforbesAntTheme.dark.colorBgLayout
            : alexforbesAntTheme.light.colorBgLayout;
        document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const { token } = useToken();

    const handleLogout = () => {
        logout();
        setShowTimeoutWarning(false);
    };

    const handleSessionTimeout = () => {
        handleLogout();
        setAlertMessage('Your session has expired due to inactivity');
        setAlertSeverity('info');
        setShowAlert(true);
    };

    const handleSessionWarning = () => {
        setShowTimeoutWarning(true);
    };

    const resetTimer = useSessionTimeout(handleSessionTimeout, handleSessionWarning);

    const handleStayLoggedIn = () => {
        resetTimer();
        setShowTimeoutWarning(false);
    };

    const toggleTheme = () => {
        setIsDarkMode(prev => !prev);
        localStorage.setItem('theme', !isDarkMode ? 'dark' : 'light');
    };

    const handleMenuClick = (key) => {
        if (key === 'logout') {
            handleLogout();
        } else {
            navigate(key);
        }
    };

    const menuItems = [
        {
            key: '/portfolios',
            icon: <PieChartOutlined />,
            label: 'Portfolios',
            onClick: () => handleMenuClick('/portfolios'),
        },
        {
            key: '/instruments',
            icon: <LineChartOutlined />,
            label: 'Instruments',
            onClick: () => handleMenuClick('/instruments'),
        },
        {
            key: '/currencies',
            icon: <DollarOutlined />,
            label: 'Currencies',
            onClick: () => handleMenuClick('/currencies'),
        },
        {
            key: '/trades',
            icon: <SwapOutlined />,
            label: 'Trades',
            onClick: () => handleMenuClick('/trades'),
        },
        {
            key: '/recon',
            icon: <FileSearchOutlined />,
            label: 'Recon',
            onClick: () => handleMenuClick('/recon'),
        },
    ];

    if (!authReady) {
        return null;
    }

    if (!isAuthenticated) {
        return (
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        );
    }

    const currentTheme = isDarkMode ? alexforbesAntTheme.dark : alexforbesAntTheme.light;
    const sidebarBg = isDarkMode ? afColors.sidebarDark : afColors.sidebar;

    return (
        <ConfigProvider
            theme={{
                algorithm: isDarkMode ? antTheme.darkAlgorithm : antTheme.defaultAlgorithm,
                token: currentTheme,
            }}
        >
            <AntLayout style={{ minHeight: '100vh', background: currentTheme.colorBgLayout }}>
                <Sider
                    theme={isDarkMode ? 'dark' : 'light'}
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        backgroundColor: sidebarBg,
                    }}
                >
                    <div className="logo">
                        <BrandLogo
                            variant={collapsed ? 'sidebarCollapsed' : 'sidebar'}
                        />
                    </div>
                    <Menu
                        theme={isDarkMode ? 'dark' : 'dark'}
                        mode="inline"
                        selectedKeys={[location.pathname]}
                        items={menuItems}
                        style={{
                            borderRight: 'none',
                            backgroundColor: sidebarBg,
                        }}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 16,
                            padding: '0 8px',
                        }}
                    >
                        <Button
                            className="sidebar-logout-btn"
                            icon={<LogoutOutlined />}
                            onClick={handleLogout}
                            block
                            danger
                            type="text"
                            style={{
                                height: 40,
                            }}
                        >
                            Logout
                        </Button>
                    </div>
                </Sider>
                <AntLayout style={{ marginLeft: collapsed ? 80 : 200 }}>
                    <Header 
                        style={{ 
                            padding: '0 16px', 
                            background: currentTheme.colorBgContainer,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                                type="text"
                                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                onClick={() => setCollapsed(!collapsed)}
                                style={{
                                    fontSize: '16px',
                                    width: 64,
                                    height: 64,
                                }}
                            />
                            <BrandLogo
                                variant="header"
                                style={{ marginLeft: 8, maxWidth: collapsed ? 140 : 300 }}
                            />
                        </div>
                        <Button
                            type="text"
                            icon={isDarkMode ? <BulbFilled /> : <BulbOutlined />}
                            onClick={toggleTheme}
                            style={{
                                fontSize: '16px',
                                color: currentTheme.colorText,
                            }}
                        />
                    </Header>
                    <Content
                        className="app-main-content"
                        style={{
                            margin: '24px 16px',
                            padding: 24,
                            height: 'calc(100vh - 112px)',
                            background: currentTheme.colorBgContainer,
                            borderRadius: token.borderRadius,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: 0,
                        }}
                    >
                        <div className="app-main-content-scroll">
                        <Routes>
                            <Route path="/" element={<Navigate to="/portfolios" replace />} />
                            <Route path="/trades" element={<TradeList />} />
                            <Route path="/instruments" element={<InstrumentList />} />
                            <Route path="/currencies" element={<CurrencyList />} />
                            <Route path="/portfolios" element={<PortfolioList />} />
                            <Route path="/portfolios/:id" element={<PortfolioDetail />} />
                            <Route path="/recon" element={<ReconPage />} />
                        </Routes>
                        </div>
                    </Content>

                    {/* Session Timeout Warning */}
                    <Snackbar
                        open={showTimeoutWarning}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    >
                        <Alert severity="warning" sx={{ width: '100%' }}>
                            <SessionTimeoutWarning
                                onStayLoggedIn={handleStayLoggedIn}
                                onLogout={handleLogout}
                            />
                        </Alert>
                    </Snackbar>

                    {/* General Alert */}
                    <Snackbar
                        open={showAlert}
                        autoHideDuration={6000}
                        onClose={() => setShowAlert(false)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={() => setShowAlert(false)}
                            severity={alertSeverity}
                            sx={{ width: '100%' }}
                        >
                            {alertMessage}
                        </Alert>
                    </Snackbar>
                </AntLayout>
            </AntLayout>
        </ConfigProvider>
    );
}

export default App; 